// StudentBillingPanel — panel bil + bayaran kongsi antara portal orangtua & guru.
// Memaparkan: senarai bil (butang Bayar), status bukti bayaran, sejarah resit + unduh PDF,
// dan dialog bayar (maklumat bank -> muat naik bukti).
// Props:
//   student: { id, nama, kelas }
//   bills: [{ id, tarifNama, periode, diskon_nota, due_date, status, baki, jumlah_bayar_rm, ... }]
//   proofs: [{ id, nama_file, status, alasan_tolak, created_at }] (untuk pelajar ini)
//   receipts: [{ id, payment_id, no_resit, drive_file_id, tanggal_cetak }] (untuk pelajar ini)
//   payments: semua payments (untuk fallback kiraan resit)
//   school: { no_akaun_bank, qr_bank_url } (maklumat bank)
//   onUploaded: callback selepas bukti berjaya dimuat naik (untuk refresh data)
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ProgressTracker from './ProgressTracker';
import { Upload, Download, Loader2, AlertTriangle, CheckCircle2, Building2, QrCode, FileText } from 'lucide-react';
import { formatRM } from '../lib/format';
import { uploadBukti, driveObjectUrl, validateProofFile } from '../lib/drive';
import { resitPdfBlob, downloadBlob } from '../lib/resitPdf';
import StatusPill from './StatusPill';
import { CenterMorphModal, CenterMorphModalContent } from './beui/CenterMorphModal';
import { Button } from './ui/button';
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from './ui/accordion';

export default function StudentBillingPanel({ student, bills, proofs, receipts, payments, school, onUploaded }) {
  const { t } = useTranslation();
  const [payBill, setPayBill] = useState(null);
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [upErr, setUpErr] = useState('');
  const [upOk, setUpOk] = useState(false);
  const [dlBusy, setDlBusy] = useState(null);

  const openPay = (bill) => { setPayBill(bill); setFile(null); setUpErr(''); setUpOk(false); };

  const doUpload = async () => {
    const err = validateProofFile(file);
    if (err) { setUpErr(err); return; }
    setUploading(true); setUpErr('');
    try {
      await uploadBukti({ file, studentId: student.id, invoiceId: payBill.id });
      setUpOk(true);
      if (onUploaded) onUploaded();
    } catch (e) { setUpErr(e.message); }
    setUploading(false);
  };

  const downloadReceipt = async (rcpt, bill) => {
    setDlBusy(rcpt.id);
    try {
      if (rcpt.drive_file_id) {
        const { url, blob } = await driveObjectUrl(rcpt.drive_file_id);
        downloadBlob(blob, `resit-${rcpt.no_resit.replace(/\//g, '-')}.pdf`);
        setTimeout(() => URL.revokeObjectURL(url), 4000);
      } else {
        // Fallback: jana PDF dari data (bayaran kaunter tanpa PDF Drive)
        const pay = (payments || []).find((p) => p.id === rcpt.payment_id) || {};
        const blob = await resitPdfBlob({
          resit: {
            no_resit: rcpt.no_resit, tanggal: rcpt.tanggal_cetak,
            studentNama: student.nama, kelas: student.kelas,
            tarifNama: bill?.tarifNama || 'Yuran', amount: Number(pay.jumlah_rm || 0),
            kaedah: pay.kaedah, baki: 0,
          }, school,
        });
        downloadBlob(blob, `resit-${rcpt.no_resit.replace(/\//g, '-')}.pdf`);
      }
    } catch (e) { setUpErr(e.message); }
    setDlBusy(null);
  };

  const hasContent = (bills || []).length > 0 || (proofs || []).length > 0 || (receipts || []).length > 0;
  if (!hasContent) return null;

  return (
    <>
      <Accordion type="single" collapsible className="mt-3">
        {(bills || []).length > 0 && (
          <AccordionItem value="bills" className="border-none">
            <AccordionTrigger className="text-sm font-semibold text-brand hover:no-underline">
              {t('lihatBil', { count: bills.length })}
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2">
                {bills.map((b) => {
                  const lewatHari = b.due_date && b.baki > 0
                    ? Math.floor((new Date(new Date().toISOString().slice(0, 10)) - new Date(b.due_date)) / 86400000)
                    : 0;
                  return (
                  <div key={b.id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2.5">
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-foreground">{b.tarifNama}{b.periode ? ` · ${b.periode}` : ''}</p>
                      {b.diskon_nota && <p className="text-xs text-emerald-600 dark:text-emerald-400">{b.diskon_nota}</p>}
                      {b.due_date && (
                        <p className="text-xs text-muted-foreground">
                          {t('tarikhAkhir')}: {b.due_date}
                          {lewatHari > 0 && (
                            <span className="ml-1.5 inline-flex items-center rounded-full bg-red-50 dark:bg-red-950 px-2 py-0.5 text-[10px] font-bold text-red-600 dark:text-red-400">
                              {t('lewatHari', { n: lewatHari })}
                            </span>
                          )}
                        </p>
                      )}
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-mono font-bold tabular text-foreground">{formatRM(b.baki > 0 ? b.baki : b.jumlah_bayar_rm)}</p>
                      <div className="mt-1.5 flex items-center justify-end gap-2">
                        <StatusPill status={b.baki > 0 ? (b.status === 'separa' ? 'separa' : 'belum') : 'lunas'} />
                        {b.baki > 0 && (
                          <button onClick={() => openPay(b)} className="h-9 px-4 rounded-lg bg-brand text-white text-xs font-bold hover:bg-brand-royal">
                            {t('bayar')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {(proofs || []).length > 0 && (
          <AccordionItem value="proofs" className="border-none">
            <AccordionTrigger className="text-sm font-semibold text-brand hover:no-underline">
              {t('statusBukti')} ({proofs.length})
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2">
                {proofs.map((p) => (
                  <div key={p.id} className="rounded-lg border border-border px-3 py-2.5 text-sm">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-foreground truncate">{p.nama_file || 'Bukti bayaran'}</span>
                      <StatusPill status={p.status} />
                    </div>
                    <p className="text-xs text-muted-foreground mt-0.5">{new Date(p.created_at).toLocaleString('ms-MY', { dateStyle: 'medium', timeStyle: 'short' })}</p>
                    <ProgressTracker proof={p} />
                    {p.status === 'tolak' && p.alasan_tolak && (
                      <p className="mt-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950 border border-red-100 rounded-lg px-2 py-1.5">
                        {t('alasanTolak')}: {p.alasan_tolak}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}

        {(receipts || []).length > 0 && (
          <AccordionItem value="receipts" className="border-none">
            <AccordionTrigger className="text-sm font-semibold text-brand hover:no-underline">
              {t('sejarahResit')} ({receipts.length})
            </AccordionTrigger>
            <AccordionContent>
              <div className="space-y-2">
                {receipts.map((r) => {
                  const pay = (payments || []).find((p) => p.id === r.payment_id) || {};
                  const bill = (bills || []).find((b) => b.id === pay.invoice_id);
                  return (
                    <div key={r.id} className="flex items-center justify-between gap-2 rounded-lg border border-border px-3 py-2.5 text-sm">
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground">#{r.no_resit}</p>
                        <p className="text-xs text-muted-foreground">{new Date(r.tanggal_cetak).toLocaleDateString('ms-MY', { dateStyle: 'medium' })} · {formatRM(pay.jumlah_rm || 0)}</p>
                      </div>
                      <button onClick={() => downloadReceipt(r, bill)} disabled={dlBusy === r.id}
                        className="h-9 px-3 rounded-lg border border-border text-xs font-bold text-muted-foreground hover:bg-muted flex items-center gap-1.5 shrink-0">
                        {dlBusy === r.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} PDF
                      </button>
                    </div>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        )}
      </Accordion>

      {/* Dialog bayar (beUI center-morph modal): maklumat bank -> upload bukti */}
      <CenterMorphModal open={!!payBill} onOpenChange={(o) => !o && setPayBill(null)}>
        <CenterMorphModalContent ariaLabel={`${t('bayar')} — ${payBill?.tarifNama || ''}`} className="max-w-md bg-card">
          <div className="p-6">
            <div className="mb-4 pr-8">
              <h2 className="text-lg font-extrabold text-foreground">{t('bayar')} — {payBill?.tarifNama}</h2>
            </div>
          {payBill && (
            <div className="space-y-4">
              <div className="rounded-xl bg-muted p-4 text-center">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{t('jumlahPerluDibayar')}</p>
                <p className="text-3xl font-black font-mono tabular text-foreground">{formatRM(payBill.baki)}</p>
                <p className="text-xs text-muted-foreground mt-1">{student.nama} · {student.kelas}</p>
              </div>

              <div className="rounded-xl border border-border p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5" />{t('pindahanBank')}</p>
                {school?.no_akaun_bank ? (
                  <p className="font-mono font-bold text-foreground">{school.no_akaun_bank}</p>
                ) : (
                  <p className="text-sm text-muted-foreground">{t('tiadaMaklumatBank')}</p>
                )}
                {school?.qr_bank_url && (
                  <div className="mt-3 flex flex-col items-center gap-1">
                    <QrCode className="h-4 w-4 text-muted-foreground" />
                    <img src={school.qr_bank_url} alt="QR bank" className="w-40 h-40 object-contain rounded-lg border border-border" />
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2">{t('arahanBayar')}</p>
              </div>

              {upOk ? (
                <div className="flex items-start gap-2 rounded-xl border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950 px-4 py-3 text-sm text-emerald-700 dark:text-emerald-300">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />{t('buktiDihantar')}
                </div>
              ) : (
                <div>
                  <label className="text-xs font-semibold text-muted-foreground">{t('muatNaikBukti')}</label>
                  <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" capture="environment"
                    onChange={(e) => { setFile(e.target.files?.[0] || null); setUpErr(''); }}
                    className="mt-1 block w-full text-sm text-muted-foreground file:mr-3 file:h-10 file:px-4 file:rounded-xl file:border-0 file:bg-brand file:text-white file:text-sm file:font-semibold hover:file:bg-brand-royal" />
                  <p className="text-[11px] text-muted-foreground mt-1">{t('hadFail')}</p>
                  {file && <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" />{file.name}</p>}
                  {upErr && <p className="mt-2 flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />{upErr}</p>}
                </div>
              )}
            </div>
          )}
          <div className="flex gap-2 mt-6">
            <Button variant="outline" onClick={() => setPayBill(null)} className="flex-1 h-11">{t('tutup')}</Button>
            {!upOk && (
              <Button onClick={doUpload} disabled={uploading || !file} className="flex-1 h-11 bg-brand text-white hover:bg-brand-royal">
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
                {t('hantarBukti')}
              </Button>
            )}
          </div>
          </div>
        </CenterMorphModalContent>
      </CenterMorphModal>

      {upErr && !payBill && (
        <p className="mt-2 flex items-start gap-1.5 text-xs text-red-600 dark:text-red-400"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />{upErr}</p>
      )}
    </>
  );
}
